import { importProvidersFrom, NO_ERRORS_SCHEMA, Injector } from '@angular/core';
import { provideRouter, withHashLocation, withPreloading, PreloadAllModules } from '@angular/router';
import { SelectivePreloadStrategy } from './services/selective-preload.strategy';
import { PerformanceMonitorService } from './services/performance-monitor.service';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';

// Core Angular Material only (essentials)
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';

// Essential third party modules only
import { NgxSpinnerModule } from 'ngx-spinner';
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ConnectionServiceModule } from 'ng-connection-service';
import { JoyrideModule } from 'ngx-joyride';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgCircleProgressModule } from 'ng-circle-progress';

// ng-icons
import { provideAppIcons } from './icons/icon-provider';

// App imports
import { routes } from './app-routing.module';
import { environment } from '../environments/environment';

// Services
import { ApiService } from './services/api.service';
import { UtilService } from './services/util.service';
import { GigsumoService } from './services/gigsumoService';
import { CacheService } from './services/cache.service';
import { MessageService } from './services/message.service';
import { CandidateService } from './services/CandidateService';
import { JobService } from './services/job.service';
import { ExcelService } from './services/excelService';
import { PreviousRouteService } from './services/PreviousRouteService';
import { CookieService } from 'ngx-cookie-service';
import { dateFormat } from './services/dateFormat';
import { TimezoneDatePipe } from './timezone-date.pipe';
import { TruncatePipe } from './pipe/TruncatePipe.pipe';

// Providers and components
import { GlobalCommunity } from './services/globalCommunity.service';
import { CurrentOrganizationProviderService } from './services/current-organization-provider.service';
import { BusinessProvider } from './guard/business-details-guard.ts.service';
import { PricePlanService } from './components/Price-subscritions/priceplanService';
import { countProvider } from './components/suggestions/suggestions.component';
import { userPlanProvider } from './services/UserService';
import { CommunicationService } from '../shared/services/CommunicationService';
import { AppHttpInterceptor } from './interceptor/AppHttpInterceptor';
import { HttpCacheInterceptor } from './interceptors/http-cache.interceptor';
import { CacheConfigService } from './services/cache-config.service';
import { CacheInvalidationService } from './services/cache-invalidation.service';
import { WidgetModule } from './widget/widget.module';

// Widgets - Handled via WidgetModule for ng-dynamic-component compatibility

export const appConfig = {
  providers: [
    environment.useHash 
      ? provideRouter(routes, withHashLocation(), withPreloading(SelectivePreloadStrategy)) 
      : provideRouter(routes, withPreloading(SelectivePreloadStrategy)),
    provideHttpClient(withInterceptorsFromDi()),
    
    // Import Angular modules that contain providers
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      ReactiveFormsModule,
      FormsModule,
      CommonModule,
      
      // Core Angular Material only
      MatButtonModule,
      MatIconModule,
      MatInputModule,
      MatMenuModule,
      MatDialogModule,

      // Essential third party modules only
      NgxSpinnerModule,
      BsDropdownModule.forRoot(),
      ModalModule.forRoot(),
      ConnectionServiceModule,
      JoyrideModule.forRoot(),
      NgbModule,
      NgCircleProgressModule.forRoot({
        radius: 100,
        outerStrokeWidth: 16,
        innerStrokeWidth: 8,
        outerStrokeColor: "#78C000",
        innerStrokeColor: "#C7E596",
        animationDuration: 300
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.name == 'QA'
      }),

      // Widget Module for ng-dynamic-component compatibility
      WidgetModule
    ),
    
    // Application services
    SelectivePreloadStrategy,
    PerformanceMonitorService,
    ApiService,
    UtilService,
    GigsumoService,
    CacheService,
    MessageService,
    CandidateService,
    JobService,
    ExcelService,
    PreviousRouteService,
    CookieService,
    dateFormat,

    // Cache services
    CacheConfigService,
    CacheInvalidationService,
    
    // Pipes
    DatePipe,
    TimezoneDatePipe,
    TruncatePipe,
    
    // Providers
    GlobalCommunity,
    CurrentOrganizationProviderService,
    BusinessProvider,
    PricePlanService,
    countProvider,
    userPlanProvider,
    CommunicationService,
    
    // HTTP Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpCacheInterceptor, multi: true },

    // ng-icons configuration
    ...provideAppIcons(),
  ],
};